import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/AdminTestimonials.css";

type SoftwareStatus = "active" | "inactive";
type ModalMode = "add" | "edit" | "view";

interface SoftwareRow {
  id: number;
  title: string;
  description: string;
  image: any;
  alt_text: string;
  video_link: string;
  sort_order: number;
  status: SoftwareStatus;
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

export default function AdminSoftware() {
  const { showModal } = useMessageModal();

  const [data, setData] = useState<SoftwareRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<SoftwareRow | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const softwareFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      {
        key: "title",
        label: "Software Title",
        type: "text",
        colSpan: 2,
      },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "image",
        label: "Software Image",
        type: "image",
        imageType: "single",
        imageFolder: "software",
        uploadField: "softwareImage",
        colSpan: 1,
      },
      {
        key: "alt_text",
        label: "Image Alt Text",
        type: "text",
        colSpan: 1,
      },
      {
        key: "video_link",
        label: "Video Link",
        type: "text",
        colSpan: 2,
      },
      {
        key: "sort_order",
        label: "Sort Order",
        type: "number",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 1,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Software Title" },
    {
      key: "image",
      label: "Image",
      imageFolder: "software",
    },
    {
      key: "description",
      label: "Description",
      render: (row) => (
        <span className="admin-testimonial-text-clamp">{row.description}</span>
      ),
    },
    {
      key: "video_link",
      label: "Video Link",
      render: (row) =>
        row.video_link ? (
          <a href={row.video_link} target="_blank" rel="noopener noreferrer">
            Watch Video
          </a>
        ) : (
          "-"
        ),
    },
    { key: "sort_order", label: "Sort Order" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
    },
  ];

  const fetchSoftware = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/software`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Software Error:", error);
    }
  };

  useEffect(() => {
    fetchSoftware();
  }, []);

  const buildFormData = (formData: any, fields: FormField[]) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value?.removed || value === "") {
          form.append(field.key, "REMOVE");
        } else if (field.key !== "image") {
          form.append(field.key, value ?? "");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, softwareFields);

      const method = modalMode === "add" ? "POST" : "PUT";
      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/software`
          : `${BASE_URL}/admin/software/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (result.success) {
        fetchSoftware();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Software added successfully."
              : "Software updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save software.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Software Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving software.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Software",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/software/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchSoftware();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Software deleted successfully.",
              confirmText: "OK",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Failed to delete software.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Software Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting software.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Software"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="software"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Software"
        fields={softwareFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
